import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/utils';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [realUser, setRealUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewAsRole, setViewAsRole] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
      setRealUser(response.data);
    } catch {
      setRealUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout request failed:', err);
    }
    setRealUser(null);
    setViewAsRole(null);
  }, []);

  useEffect(() => {
    // CRITICAL (Emergent Auth playbook): If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // Calling /auth/me here races against the cookie being set and can intermittently
    // overwrite the freshly-authenticated user with null (causes "login failed, try again").
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // When viewAsRole is set, override the role in the user object
  const user = useMemo(() => {
    if (!realUser) return null;
    if (!viewAsRole || viewAsRole === realUser.role) return realUser;
    return { ...realUser, role: viewAsRole };
  }, [realUser, viewAsRole]);

  const isViewingAs = !!(viewAsRole && realUser && viewAsRole !== realUser.role);
  const realRole = realUser?.role;

  // logout and checkAuth are useCallback'd with empty deps → stable identity, omitted from deps.
  // setRealUser is from useState → guaranteed stable, also omitted.
  const contextValue = useMemo(() => ({
    user, setUser: setRealUser, loading, logout, checkAuth,
    viewAsRole, setViewAsRole, isViewingAs, realRole
  }), [user, loading, viewAsRole, isViewingAs, realRole]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
